import { APPLE_PROVIDER_TOKEN } from "./config";

/* ============================================================================
   Where did this visit come from?

   On the first page of a visit we work out:
     landing_page    first page path of the visit, e.g. "/sv/ovningsprov/"
     traffic_source  "<source> / <medium>", e.g. "google / organic",
                     "instagram / social" (from UTM), "chatgpt.com / referral",
                     or "direct"
     campaign        utm_campaign, or "(none)"

   It's kept in sessionStorage (cleared when the tab closes) so later pages and
   clicks in the same visit carry the same values. Only used after consent.

   Campaign links: add UTM parameters to any URL you share, e.g.
     https://teorikorkort.se/uk/?utm_source=facebook&utm_medium=social&utm_campaign=ukraine_groups
   ========================================================================== */

const SESSION_KEY = "tk_visit";
const SEARCH_ENGINES = ["google", "bing", "duckduckgo", "yahoo", "yandex", "ecosia", "baidu", "qwant", "startpage"];

/* Keep campaign-style text short and free of anything that could be personal. */
export function cleanLabel(value, maxLength = 60) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9._\- ]/g, "") // drops @, /, ?, = … so emails or URLs can't slip through
    .trim()
    .slice(0, maxLength);
}

function sourceFromReferrer(referrer) {
  if (!referrer) return "direct";
  let host;
  try { host = new URL(referrer).hostname.replace(/^www\./, ""); } catch (e) { return "direct"; }
  if (host === window.location.hostname.replace(/^www\./, "")) return "direct";
  const engine = SEARCH_ENGINES.find((name) => host.includes(`${name}.`));
  return engine ? `${engine} / organic` : `${cleanLabel(host)} / referral`;
}

function readVisit() {
  try { return JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || "null"); } catch (e) { return null; }
}

/* The visit's landing page, source and campaign (works out and saves them on the first page). */
export function getVisit() {
  const saved = readVisit();
  if (saved) return saved;

  const params = new URLSearchParams(window.location.search);
  const utmSource = cleanLabel(params.get("utm_source"));
  const utmMedium = cleanLabel(params.get("utm_medium"));
  const visit = {
    landing_page: window.location.pathname, // path only: query strings can contain personal data
    traffic_source: utmSource ? `${utmSource} / ${utmMedium || "(not set)"}` : sourceFromReferrer(document.referrer),
    campaign: cleanLabel(params.get("utm_campaign")) || "(none)",
  };
  try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(visit)); } catch (e) { /* ignore */ }
  return visit;
}

/* "ios" | "android" | "desktop" — the device the visitor is on (not the store they click). */
export function getPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/* Language of the current page, from the URL ("/uk/…" → "uk"); "sv" for "/". */
export function getPageLanguage() {
  const match = window.location.pathname.match(/^\/(sv|en|uk|ru)(\/|$)/);
  return match ? match[1] : document.documentElement.lang || "sv";
}

/* ----------------------------------------------------------------------------
   Store links carry campaign info so the stores can report installs from the
   website (see docs/ANALYTICS.md → "Website-to-app attribution"):
     Google Play: referrer=utm_source=teorikorkort.se&utm_medium=website&utm_campaign=…
                  (readable later by the app via the Play Install Referrer API)
     App Store:   pt=<provider token>&ct=<campaign>  (App Store Connect → App Analytics)
   This adds no personal data, so it is done for every visitor.
---------------------------------------------------------------------------- */
export function withStoreAttribution(href, store, placement) {
  let url;
  try { url = new URL(href); } catch (e) { return href; }

  // Campaign of the visit if it came from one, otherwise where on the site the button was.
  const visitCampaign = readVisit()?.campaign;
  const campaign = cleanLabel(visitCampaign && visitCampaign !== "(none)" ? visitCampaign : `web_${placement}`, 40);

  if (store === "google_play") {
    url.searchParams.set("referrer", `utm_source=teorikorkort.se&utm_medium=website&utm_campaign=${campaign}`);
  } else if (store === "app_store" && APPLE_PROVIDER_TOKEN) {
    url.searchParams.set("pt", APPLE_PROVIDER_TOKEN);
    url.searchParams.set("ct", campaign);
    url.searchParams.set("mt", "8");
  }
  return url.toString();
}
